import { useState, useEffect, useRef } from 'react';
import { Search, Users } from 'lucide-react';
import PersonRow from '../../atoms/PersonRow/view';
import Empty from '../../atoms/empty/view';
import './style.css';
import NewThird from '../newThird/view';

function PersonCardGrid({
    people = [],
    total = 0,
    page = 1,
    pageSize = 20,
    variant = 'grid',
    onPageChange,
    onSearch,
    searchValue = '',
    initialThird = {},
    searchPlaceholder = 'Buscar por nombre, documento o email...',
    onAddPerson,
    addLabel = 'Agregar',
    emptyTitle = 'No se encontraron personas',
    emptyDescription = 'No hay resultados con los filtros actuales.',
    renderPersonCard,
    renderActions,
    className = '',
    isLoading = false,
}) {
    const [search, setSearch] = useState(searchValue || '');
    const containerRef = useRef(null);
    const totalPages = Math.ceil(total / pageSize);
    const isRows = variant === 'rows';
    const hasMore = page < totalPages;

    useEffect(() => {
        setSearch(searchValue || '');
    }, [searchValue]);

    const handleSearch = (e) => {
        const val = e.target.value;
        setSearch(val);
        onSearch?.(val);
    };


    // Scroll listener for pagination
    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current;
            if (!container) return;

            const scrollTop = container.scrollTop;
            const clientHeight = container.clientHeight;
            const scrollHeight = container.scrollHeight;
            const maxScroll = scrollHeight - clientHeight;

            // Trigger when scrolled to 85% of the scroll height
            if (maxScroll > 0 && (scrollTop / maxScroll) >= 0.85) {
                if (hasMore && !isLoading && onPageChange) {
                    onPageChange(page + 1);
                }
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll, { passive: true });
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [page, hasMore, isLoading, onPageChange]);

    return (
        <div style={{ height: "55vh", maxHeight: "55vh", overflowY: "hidden" }}>
            <style>{`
                @keyframes grid-spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
            <div className="person-grid-toolbar">
                <div className="person-grid-search">
                    <Search size={18} className="person-grid-search-icon" />
                    <input
                        type="text"
                        className="person-grid-search-input"
                        placeholder={searchPlaceholder}
                        value={search}
                        onChange={handleSearch}
                    />
                </div>
                <div className="person-grid-info">
                    <span className="person-grid-count">{total} personas</span>
                    <NewThird initialData={initialThird}></NewThird>
                </div>

            </div>
            <div ref={containerRef} className={`person-grid ${className}`}>

                {isLoading && people.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', padding: '16px 0' }}>
                        <style>{`
                            @keyframes skeleton-loading {
                                0% { background-position: 200% 0; }
                                100% { background-position: -200% 0; }
                            }
                            .skeleton-row-item {
                                display: flex;
                                align-items: center;
                                justify-content: space-between;
                                padding: 16px;
                                background: var(--color-surface, #ffffff);
                                border-radius: 12px;
                                margin-bottom: 12px;
                                border: 1px solid var(--color-border, #f0f0f0);
                            }
                            .skeleton-avatar {
                                width: 44px;
                                height: 44px;
                                border-radius: 50%;
                                background: linear-gradient(90deg, var(--color-gray-10, #f5f5f5) 25%, var(--color-gray-30, #e8e8e8) 50%, var(--color-gray-10, #f5f5f5) 75%);
                                background-size: 200% 100%;
                                animation: skeleton-loading 1.5s infinite;
                            }
                            .skeleton-info {
                                flex: 1;
                                margin-left: 16px;
                                display: flex;
                                flex-direction: column;
                                gap: 8px;
                            }
                            .skeleton-text-title {
                                width: 180px;
                                height: 16px;
                                border-radius: 4px;
                                background: linear-gradient(90deg, var(--color-gray-10, #f5f5f5) 25%, var(--color-gray-30, #e8e8e8) 50%, var(--color-gray-10, #f5f5f5) 75%);
                                background-size: 200% 100%;
                                animation: skeleton-loading 1.5s infinite;
                            }
                            .skeleton-text-subtitle {
                                width: 280px;
                                height: 12px;
                                border-radius: 4px;
                                background: linear-gradient(90deg, var(--color-gray-10, #f5f5f5) 25%, var(--color-gray-30, #e8e8e8) 50%, var(--color-gray-10, #f5f5f5) 75%);
                                background-size: 200% 100%;
                                animation: skeleton-loading 1.5s infinite;
                            }
                            .skeleton-actions {
                                display: flex;
                                gap: 8px;
                            }
                            .skeleton-btn {
                                width: 34px;
                                height: 34px;
                                border-radius: 8px;
                                background: linear-gradient(90deg, var(--color-gray-10, #f5f5f5) 25%, var(--color-gray-30, #e8e8e8) 50%, var(--color-gray-10, #f5f5f5) 75%);
                                background-size: 200% 100%;
                                animation: skeleton-loading 1.5s infinite;
                            }
                            [data-theme='dark'] .skeleton-row-item {
                                background: var(--color-gray-20, #1e293b);
                                border-color: var(--color-gray-30, #334155);
                            }
                            [data-theme='dark'] .skeleton-avatar,
                            [data-theme='dark'] .skeleton-text-title,
                            [data-theme='dark'] .skeleton-text-subtitle,
                            [data-theme='dark'] .skeleton-btn {
                                background: linear-gradient(90deg, #1e293b 25%, #334155 50%, #1e293b 75%);
                                background-size: 200% 100%;
                            }


                        `}</style>
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="skeleton-row-item">
                                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                    <div className="skeleton-avatar" style={{ animationDelay: `${i * 0.15}s` }} />
                                    <div className="skeleton-info">
                                        <div className="skeleton-text-title" style={{ animationDelay: `${i * 0.15}s` }} />
                                        <div className="skeleton-text-subtitle" style={{ animationDelay: `${i * 0.15}s` }} />
                                    </div>
                                </div>
                                <div className="skeleton-actions">
                                    <div className="skeleton-btn" style={{ animationDelay: `${i * 0.15}s` }} />
                                    <div className="skeleton-btn" style={{ animationDelay: `${i * 0.15}s` }} />
                                    <div className="skeleton-btn" style={{ animationDelay: `${i * 0.15}s` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : people.length === 0 || (people.length === 1 && people[0]?.message === 'empty_array') ? (
                    <Empty
                        icon={<Users size={28} />}
                        title={emptyTitle}
                        description={emptyDescription}
                        action={onAddPerson ? (
                            <button className="person-grid-add-btn" onClick={onAddPerson}>
                                + {addLabel}
                            </button>
                        ) : null}
                    />
                ) : (
                    <>
                        <div className="person-grid-rows">
                             {people.map((person, i) => {
                                 const data = typeof renderPersonCard === 'function' ? renderPersonCard(person) : person;
                                 return (
                                     <PersonRow
                                         key={person.id || i}
                                         name={data.name}
                                         subtitle={data.subtitle}
                                         status={data.status}
                                         statusLabel={data.statusLabel}
                                         avatarUrl={data.avatarUrl}
                                         avatarInitial={data.avatarInitial}
                                         onClick={data.onClick}
                                         renderActions={typeof renderActions === 'function' ? renderActions(person) : null}
                                     />
                                 );
                             })}
                        </div>
                        {isLoading && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '16px 0', color: 'var(--color-gray-50)', fontSize: '0.85rem' }}>
                                <div style={{ width: '16px', height: '16px', border: '2px solid var(--color-gray-30)', borderTopColor: 'var(--color-primary)', borderRadius: '50%', animation: 'grid-spin 1s linear infinite' }} />
                                <span>Cargando más...</span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default PersonCardGrid;
export { PersonCardGrid };
